import { Tabs } from "@mantine/core";
import { useNavigate } from "react-router";
import { usePermission } from "@how/app-kit";
import { OfflineBanner } from "./OfflineBanner";

export type WebsiteTab =
  | "pages"
  | "menus"
  | "images"
  | "look"
  | "forms"
  | "sermons"
  | "devotionals"
  | "domain"
  | "settings";

const PATHS: Record<WebsiteTab, string> = {
  pages: "/website",
  menus: "/website/menus",
  images: "/website/images",
  look: "/website/look",
  forms: "/website/forms",
  sermons: "/website/sermons",
  devotionals: "/website/devotionals",
  domain: "/website/domain",
  settings: "/website/settings",
};

/**
 * Pages, menus, images, look and forms are one section: the church's public site seen from each side. Domain is
 * the owner's (settings.manage), so it is shown only to someone who can use it (ADR 0051). Sermons (ADR 0061) shows
 * to someone with sermons.manage in a church with sermons and media. Devotionals (ADR 0077) shows to someone with
 * devotionals.read in a church with devotionals; editing them asks for devotionals.manage on the screen. Settings
 * (ADR 0079: the website offline or online, the bypass link) shows to someone with website.manage, and the banner
 * above the tabs tells the same people, on every Website screen, while the website is offline.
 */
export function WebsiteTabs({ active }: { readonly active: WebsiteTab }) {
  const navigate = useNavigate();
  const canManageForms = usePermission("forms.manage");
  const canManageSettings = usePermission("settings.manage");
  const canManageSermons = usePermission("sermons.manage", { module: "sermons_media" });
  const canReadDevotionals = usePermission("devotionals.read", { module: "devotionals" });
  const canManageWebsite = usePermission("website.manage");
  return (
    <>
      {canManageWebsite.data === true ? <OfflineBanner active={active} /> : null}
      <Tabs
        value={active}
        onChange={(value) => {
          if (value !== null && value in PATHS) void navigate(PATHS[value as WebsiteTab]);
        }}
        mb="lg"
      >
        <Tabs.List>
          <Tabs.Tab value="pages">Pages</Tabs.Tab>
          <Tabs.Tab value="menus">Menus</Tabs.Tab>
          <Tabs.Tab value="images">Images</Tabs.Tab>
          <Tabs.Tab value="look">Look</Tabs.Tab>
          {canManageForms.data === true ? <Tabs.Tab value="forms">Forms</Tabs.Tab> : null}
          {canManageSermons.data === true ? <Tabs.Tab value="sermons">Sermons</Tabs.Tab> : null}
          {canReadDevotionals.data === true ? (
            <Tabs.Tab value="devotionals">Devotionals</Tabs.Tab>
          ) : null}
          {canManageSettings.data === true ? <Tabs.Tab value="domain">Domain</Tabs.Tab> : null}
          {canManageWebsite.data === true ? <Tabs.Tab value="settings">Settings</Tabs.Tab> : null}
        </Tabs.List>
      </Tabs>
    </>
  );
}
